






































































































import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { AxiosError } from 'axios';

import { ALForm } from '@/common/component.types';
import {
  IQuestion,
  IQuestionTag,
  IQuestionTagData,
  ITag,
  ITagData,
  QuestionTag
} from '@/models/configurator';
import { ISystemError, SystemError } from '@/models/error/index';
import { getPropertyFromItem, setErrorFromAxiosError } from '@/utils/helpers';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import QuestionTagPicker from './QuestionTagPicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';

export interface Options extends InstanceType<typeof Vue> {
  $refs: {
    form: ALForm;
  };
}

export default Vue.extend<Options>({
  functional: false
}).extend({
  name: 'QuestionTagsFormDialog',

  components: {
    ALDialog,
    ErrorAlert,
    QuestionTagPicker
  },

  props: {
    module: { type: Number }
  },

  data() {
    return {
      error: null as ISystemError | null,
      question: null as IQuestion | null,

      isDialogActive: false,
      isLoading: false,
      isFormValid: false,

      requiredRule: (v: string) => !!v || 'Field is required',
      questionTags: [] as IQuestionTag[],

      questionTagData: {} as IQuestionTagData
    };
  },

  computed: {
    ...mapGetters({
      getTagById: 'configuratorQuestion/getTagById',
      getQuestionById: 'configuratorQuestion/getQuestionById'
    }),

    selectedTag(): ITag | undefined {
      if (!this.questionTagData.tag) return undefined;
      return this.getTagById(
        this.getTagValue(this.questionTagData.tag as ITag)
      );
    },

    duplicateTagRule() {
      return (value: any) => {
        if (value) {
          const tagIndex = this.questionTags.findIndex(
            questionTag => this.getTagValue(questionTag.tag) === value
          );

          return tagIndex === -1 || 'This tag is already in use';
        }
        return true;
      };
    }
  },

  mounted() {
    this.fetchTags();
  },

  methods: {
    ...mapActions({
      fetchTags: 'configuratorQuestion/fetchTags',
      addQuestionTag: 'configuratorQuestion/addQuestionTag',
      removeQuestionTag: 'configuratorQuestion/removeQuestionTag'
    }),

    validateForm(): boolean {
      let valid = false;
      if (this.$refs.form) {
        valid = this.$refs.form.validate();
      }
      return valid;
    },

    resetValidation(): void {
      if (this.$refs.form) {
        return this.$refs.form.resetValidation();
      }
    },

    clearErrors() {
      this.error = null;
    },

    close() {
      this.isDialogActive = false;
    },

    open(question: IQuestion) {
      this.clearErrors();

      this.question = cloneDeep(question);
      this.questionTags = cloneDeep(this.question.tags).map(
        tag => new QuestionTag(tag)
      );
      this.questionTagData = {} as ITagData;

      this.isDialogActive = true;

      this.resetValidation();
    },

    async removeTag(tag: IQuestionTag, index: number) {
      try {
        this.isLoading = true;
        await this.removeQuestionTag({
          module: this.module,
          id: this.question!.id,
          tag: (tag.tag as ITag).id
        });

        if (index > -1) {
          this.questionTags.splice(index, 1);
        }
      } catch (error) {
        this.error = setErrorFromAxiosError(error as AxiosError);
      } finally {
        this.isLoading = false;
      }
    },

    saveQuestionTag() {
      this.clearErrors();

      if (this.validateForm()) {
        let tagInstance: IQuestionTag | null = null;
        try {
          tagInstance = new QuestionTag(this.questionTagData);
        } catch (error) {
          if (SystemError.isSystemError(error)) {
            this.error = error;
          }
        }
        if (tagInstance != null) {
          this.isLoading = true;

          this.addQuestionTag({
            module: this.module,
            id: this.question!.id,
            tag: this.questionTagData
          })
            .then(() => {
              this.questionTagData = {} as ITagData;

              this.questionTags = cloneDeep<IQuestion>(
                this.getQuestionById(this.question!.module, this.question!.id)
              ).tags.map(tag => new QuestionTag(tag));

              this.$nextTick(() => {
                this.resetValidation();
              });
            })
            .catch(error => {
              this.error = setErrorFromAxiosError(error);
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      }
    },

    getTagValue(tag: ITag | number): number {
      return getPropertyFromItem(tag as ITag, 'id', tag);
    }
  }
});
